import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import api from '../../api';

const NAVY = [27, 67, 113];
const TEXT = [34, 52, 70];
const MUTED = [91, 108, 123];
const BORDER = [181, 195, 207];

const text = (value) => String(value ?? '').trim();
const compact = (items) => items.map(text).filter(Boolean);

const blobToDataUrl = (blob) => new Promise((resolve) => {
  if (!blob || typeof FileReader === 'undefined') return resolve(null);
  const reader = new FileReader();
  reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
  reader.onerror = () => resolve(null);
  reader.readAsDataURL(blob);
});

const organizationLines = (organization) => {
  const address = compact([
    organization?.addressLine1,
    organization?.addressLine2,
    compact([organization?.city, organization?.state]).join(' - '),
    organization?.zipCode,
  ]).join(', ');
  const contact = compact([
    organization?.phone ? `Telefone ${organization.phone}` : '',
    organization?.whatsapp ? `WhatsApp ${organization.whatsapp}` : '',
    organization?.email,
    organization?.website,
  ]);
  return {
    name: text(organization?.tradeName) || text(organization?.name) || 'Organização',
    address,
    contact,
  };
};

const loadLogoData = async (organization) => {
  if (!organization?.logoUrl) return null;
  try {
    const response = await api.get('/company/logo/content', { responseType: 'blob' });
    return await blobToDataUrl(response?.data);
  } catch {
    return null;
  }
};

const createHost = (html) => {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.position = 'fixed';
  host.style.left = '-20000px';
  host.style.top = '0';
  host.style.width = '794px';
  host.style.padding = '28px 36px 38px';
  host.style.boxSizing = 'border-box';
  host.style.background = '#fff';
  host.style.color = '#23384b';
  host.style.fontFamily = 'Montserrat, Arial, sans-serif';
  host.style.fontSize = '11px';
  host.style.lineHeight = '1.52';
  host.innerHTML = html;

  host.querySelectorAll('table').forEach((table) => {
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
  });
  host.querySelectorAll('tr').forEach((row) => {
    row.style.breakInside = 'avoid';
    row.style.pageBreakInside = 'avoid';
  });
  host.querySelectorAll('th, td').forEach((cell) => {
    if (!cell.style.border) cell.style.border = '1px solid #aebdca';
    if (!cell.style.padding) cell.style.padding = '7px 8px';
    if (!cell.style.verticalAlign) cell.style.verticalAlign = 'top';
  });
  host.querySelectorAll('th').forEach((cell) => {
    if (!cell.style.fontWeight) cell.style.fontWeight = '700';
  });

  document.body.appendChild(host);
  return host;
};

const imageFormat = (dataUrl) => {
  if (/^data:image\/png/i.test(dataUrl || '')) return 'PNG';
  if (/^data:image\/webp/i.test(dataUrl || '')) return 'WEBP';
  return 'JPEG';
};

const fitLogo = (doc, logoData, box) => {
  try {
    const properties = doc.getImageProperties(logoData);
    const sourceWidth = Number(properties?.width || 0);
    const sourceHeight = Number(properties?.height || 0);
    if (!(sourceWidth > 0) || !(sourceHeight > 0)) return box;

    const ratio = sourceWidth / sourceHeight;
    let width = box.width;
    let height = width / ratio;
    if (height > box.height) {
      height = box.height;
      width = height * ratio;
    }

    return {
      x: box.x + (box.width - width) / 2,
      y: box.y + (box.height - height) / 2,
      width,
      height,
    };
  } catch {
    return box;
  }
};

const drawHeader = ({ doc, organization, logoData, pageWidth }) => {
  const margin = 11;
  const info = organizationLines(organization);

  doc.setDrawColor(...NAVY);
  doc.setLineWidth(1.45);
  doc.line(margin, 4.5, pageWidth - margin, 4.5);

  const logoBox = { x: margin, y: 7.5, width: 43, height: 18.5 };
  if (logoData) {
    try {
      const fitted = fitLogo(doc, logoData, logoBox);
      doc.addImage(
        logoData,
        imageFormat(logoData),
        fitted.x,
        fitted.y,
        fitted.width,
        fitted.height,
        undefined,
        'FAST',
      );
    } catch {
      doc.setTextColor(...NAVY);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.text(info.name, margin, 17, { maxWidth: pageWidth * 0.42 });
    }
  } else {
    doc.setTextColor(...NAVY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.text(info.name, margin, 17, { maxWidth: pageWidth * 0.42 });
  }

  doc.setTextColor(...TEXT);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.2);
  let y = 9.2;
  const right = pageWidth - margin;
  if (info.address) {
    const lines = doc.splitTextToSize(info.address, pageWidth * 0.4).slice(0, 2);
    doc.text(lines, right, y, { align: 'right' });
    y += Math.max(4, lines.length * 3 + 1);
  }
  info.contact.slice(0, 4).forEach((line) => {
    doc.text(line, right, y, { align: 'right', maxWidth: pageWidth * 0.4 });
    y += 3.4;
  });

  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.25);
  doc.line(margin, 31.5, pageWidth - margin, 31.5);
};

const inkRatioAt = (context, width, y) => {
  if (y < 0) return 0;
  const pixels = context.getImageData(0, y, width, 1).data;
  let ink = 0;
  let samples = 0;
  const step = 12;
  for (let x = 0; x < width; x += step) {
    const index = x * 4;
    const red = pixels[index];
    const green = pixels[index + 1];
    const blue = pixels[index + 2];
    if (red < 238 || green < 238 || blue < 238) ink += 1;
    samples += 1;
  }
  return samples ? ink / samples : 0;
};

const findSafeSliceEnd = (canvas, start, targetHeight) => {
  const desired = Math.min(canvas.height, start + targetHeight);
  if (desired >= canvas.height) return canvas.height;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return desired;

  const lowerBound = Math.max(start + Math.floor(targetHeight * 0.72), desired - Math.floor(targetHeight * 0.22));
  let blankRun = 0;
  let closestDivider = null;

  for (let y = desired; y >= lowerBound; y -= 1) {
    const ratio = inkRatioAt(context, canvas.width, y);
    if (ratio < 0.018) {
      blankRun += 1;
      if (blankRun >= 5) return Math.max(start + 1, y + 4);
    } else {
      blankRun = 0;
    }

    // Linhas horizontais de tabelas são pontos de quebra melhores do que cortar texto.
    if (closestDivider === null && ratio > 0.62) closestDivider = y + 1;
  }

  if (closestDivider && closestDivider > start + Math.floor(targetHeight * 0.68)) return closestDivider;
  return desired;
};

export async function downloadYoungApprenticeProgramReportPdf({
  organization,
  contentHtml,
  filename = 'relatorio-do-programa.pdf',
}) {
  if (!contentHtml) throw new Error('O Relatório do Programa está vazio.');
  const host = createHost(contentHtml);
  try {
    if (document.fonts?.ready) await document.fonts.ready;
    const [canvas, logoData] = await Promise.all([
      html2canvas(host, {
        backgroundColor: '#ffffff',
        scale: 1.9,
        useCORS: true,
        logging: false,
        windowWidth: host.scrollWidth,
        windowHeight: host.scrollHeight,
      }),
      loadLogoData(organization),
    ]);

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 11;
    const contentTop = 35;
    const footerTop = pageHeight - 10;
    const usableWidth = pageWidth - marginX * 2;
    const usableHeight = footerTop - contentTop - 2;
    const pxPerMm = canvas.width / usableWidth;
    const targetSlicePx = Math.max(1, Math.floor(usableHeight * pxPerMm));

    let top = 0;
    let page = 0;
    while (top < canvas.height) {
      const bottom = findSafeSliceEnd(canvas, top, targetSlicePx);
      const height = Math.max(1, bottom - top);
      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = height;
      const context = slice.getContext('2d');
      context?.drawImage(canvas, 0, top, canvas.width, height, 0, 0, canvas.width, height);

      if (page > 0) doc.addPage();
      drawHeader({ doc, organization, logoData, pageWidth });
      const renderedHeight = height / pxPerMm;
      doc.addImage(slice.toDataURL('image/jpeg', 0.96), 'JPEG', marginX, contentTop, usableWidth, renderedHeight, undefined, 'FAST');

      top = bottom;
      page += 1;
    }

    const totalPages = doc.getNumberOfPages();
    for (let currentPage = 1; currentPage <= totalPages; currentPage += 1) {
      doc.setPage(currentPage);
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.2);
      doc.line(marginX, pageHeight - 9, pageWidth - marginX, pageHeight - 9);
      doc.setTextColor(...MUTED);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.2);
      doc.text('Relatório do Programa', marginX, pageHeight - 5.3);
      doc.text(`Página ${currentPage} de ${totalPages}`, pageWidth - marginX, pageHeight - 5.3, { align: 'right' });
    }

    doc.save(filename);
  } finally {
    host.remove();
  }
}

export default downloadYoungApprenticeProgramReportPdf;
